<template>
  <div class="login-page min-vh-100 d-flex align-items-center">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-8 col-lg-6 col-xl-5">
          <div class="card shadow-lg border-0 rounded-3">
            <div class="card-body p-5">
              <!-- Logo and Title -->
              <div class="text-center mb-4">
                <div class="mb-3">
                  <HeartIcon class="logo-icon" />
                </div>
                <h2 class="fw-bold text-dark mb-2">Welcome Back</h2>
                <p class="text-muted">Sign in to your FemWellness account</p>
              </div>

              <!-- Alert Messages -->
              <div
                v-if="alertMessage"
                :class="alertClass"
                class="alert alert-dismissible fade show"
                role="alert"
              >
                <span v-html="escapeForDisplay(alertMessage)"></span>
                <button
                  type="button"
                  class="btn-close"
                  @click="clearAlert"
                  aria-label="Close"
                ></button>
              </div>
              
              <!-- Rate Limit Warning -->
              <div
                v-if="isBlocked"
                class="alert alert-warning d-flex align-items-center"
                role="alert"
              >
                <ExclamationTriangleIcon class="alert-icon me-2" />
                Too many failed attempts. Please try again in {{ formatTimeRemaining(blockTimeRemaining) }}.
              </div>

              <!-- Login Form -->
              <form @submit.prevent="handleLogin" novalidate>
                <!-- Email Field -->
                <div class="mb-3">
                  <label for="email" class="form-label fw-semibold">
                    Email Address <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-group-text">
                      <EnvelopeIcon class="input-icon" />
                    </span>
                    <input
                      type="email"
                      class="form-control"
                      :class="{ 'is-invalid': errors.email, 'is-valid': email && !errors.email }"
                      id="email"
                      v-model="email"
                      @blur="validateEmail"
                      @input="clearFieldError('email')"
                      placeholder="Enter your email"
                      required
                    />
                    <div v-if="errors.email" class="invalid-feedback">
                      {{ errors.email }}
                    </div>
                  </div>
                </div>

                <!-- Password Field -->
                <div class="mb-3">
                  <label for="password" class="form-label fw-semibold">
                    Password <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-group-text">
                      <LockClosedIcon class="input-icon" />
                    </span>
                    <input
                      :type="showPassword ? 'text' : 'password'"
                      class="form-control"
                      :class="{
                        'is-invalid': errors.password,
                        'is-valid': password && !errors.password,
                      }"
                      id="password"
                      v-model="password"
                      @blur="validatePassword"
                      @input="clearFieldError('password')"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      class="btn btn-outline-secondary"
                      type="button"
                      @click="togglePassword"
                      aria-label="Toggle password visibility"
                    >
                      <EyeSlashIcon v-if="showPassword" class="input-icon" />
                      <EyeIcon v-else class="input-icon" />
                    </button>
                    <div v-if="errors.password" class="invalid-feedback">
                      {{ errors.password }}
                    </div>
                  </div>
                </div>

                <!-- Remember Me and Forgot Password -->
                <div class="d-flex justify-content-between align-items-center mb-4">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="rememberMe"
                      v-model="rememberMe"
                    />
                    <label class="form-check-label text-muted" for="rememberMe">
                      Remember me
                    </label>
                  </div>
                  <a
                    href="#"
                    class="text-primary text-decoration-none small"
                    @click="forgotPassword"
                  >
                    Forgot password?
                  </a>
                </div>

                <!-- Login Button -->
                <button
                  type="submit"
                  class="btn btn-primary w-100 py-2 mb-3"
                  :disabled="isLoading || !isFormValid || isBlocked"
                >
                  <span
                    v-if="isLoading"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {{ isLoading ? 'Signing in...' : isBlocked ? 'Account Temporarily Locked' : 'Sign In' }}
                </button>

                <!-- Social Login Options -->
                <div class="text-center mb-3">
                  <small class="text-muted">Or sign in with</small>
                </div>

                <div class="mb-3">
                  <button
                    type="button"
                    class="btn btn-outline-dark w-100"
                    @click="handleGoogleLogin"
                    :disabled="isLoading"
                  >
                    <svg class="social-icon me-2" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    <span v-if="isLoading">Signing in...</span>
                    <span v-else>Google</span>
                  </button>
                </div>

                <!-- Sign Up Link -->
                <div class="text-center">
                  <p class="text-muted mb-0">
                    Don't have an account?
                    <router-link
                      to="/register"
                      class="text-primary text-decoration-none fw-semibold"
                    >
                      Sign up here
                    </router-link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'
import { useValidation } from '@/composables/useValidation'
import { useSecurity } from '@/composables/useSecurity'
import { 
  HeartIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon
} from '@/utils/icons'

export default {
  name: 'Login',
  components: {
    HeartIcon,
    ExclamationTriangleIcon,
    EnvelopeIcon,
    LockClosedIcon,
    EyeIcon,
    EyeSlashIcon,
  },
  setup() {
    const { login, loginWithGoogle } = useFirebaseAuth()
    const { validateEmail: validateEmailField, validateRequired, sanitizeFormData } = useValidation()
    const {
      isBlocked,
      blockTimeRemaining,
      checkRateLimit,
      recordFailedAttempt,
      resetRateLimit,
      escapeForDisplay,
      formatTimeRemaining
    } = useSecurity()

    return {
      firebaseLogin: login,
      firebaseLoginWithGoogle: loginWithGoogle,
      validateEmailField,
      validateRequired,
      sanitizeFormData,
      isBlocked,
      blockTimeRemaining,
      checkRateLimit,
      recordFailedAttempt,
      resetRateLimit,
      escapeForDisplay,
      formatTimeRemaining
    }
  },
  data() {
    return {
      email: '',
      password: '',
      rememberMe: false,
      showPassword: false,
      isLoading: false,
      alertMessage: '',
      alertType: '',
      errors: {},
    }
  },
  computed: {
    isFormValid() {
      return this.email && this.password && !this.errors.email && !this.errors.password
    },
    alertClass() {
      return this.alertType === 'success' ? 'alert-success' : 'alert-danger'
    },
  },
  mounted() {
    // Check if user is rate limited when component mounts
    if (this.email) {
      this.checkRateLimit(this.email)
    }
  },
  methods: {
    validateEmail() {
      const result = this.validateEmailField(this.email, 'email')
      if (result) {
        delete this.errors.email
      } else {
        // Error is already set by validateEmailField
      }
      this.$forceUpdate()
    },
    validatePassword() {
      const result = this.validateRequired(this.password, 'password')
      if (!result) {
        this.errors.password = 'Password is required'
      } else if (this.password.length < 6) {
        this.errors.password = 'Password must be at least 6 characters'
      } else {
        delete this.errors.password
      }
      this.$forceUpdate()
    },
    clearFieldError(field) {
      if (this.errors[field]) {
        delete this.errors[field]
        this.$forceUpdate()
      }
    },
    togglePassword() {
      this.showPassword = !this.showPassword
    },
    clearAlert() {
      this.alertMessage = ''
      this.alertType = ''
    },
    async handleLogin() {
      // Clear previous alerts
      this.clearAlert()

      // Check rate limiting
      if (!this.checkRateLimit(this.email)) {
        this.showAlert('Too many failed attempts. Please wait before trying again.', 'error')
        return
      }

      // Sanitize input data
      const sanitizedData = this.sanitizeFormData({
        email: this.email,
        password: this.password
      })

      // Update form with sanitized data
      this.email = sanitizedData.email
      this.password = sanitizedData.password

      // Validate form
      this.validateEmail()
      this.validatePassword()

      if (!this.isFormValid) {
        this.showAlert('Please fix the errors above', 'error')
        return
      }

      this.isLoading = true

      try {
        // Use Firebase authentication with sanitized data
        const result = await this.firebaseLogin(this.email, this.password)

        if (result.success) {
          // Reset rate limiting on successful login
          this.resetRateLimit(this.email)
          
          this.showAlert('Login successful! Redirecting...', 'success')

          // Redirect after successful login
          setTimeout(() => {
            const redirectPath = this.$route.query.redirect || '/'
            this.$router.push(redirectPath)
          }, 1500)
        } else {
          // Record failed attempt for rate limiting
          this.recordFailedAttempt(this.email)
          
          const errorMessage = this.escapeForDisplay(result.error || 'Invalid email or password. Please try again.')
          this.showAlert(errorMessage, 'error')
        }
      } catch (error) {
        // Record failed attempt for rate limiting
        this.recordFailedAttempt(this.email)
        
        console.error('Login error:', error)
        this.showAlert('An error occurred during login. Please try again.', 'error')
      } finally {
        this.isLoading = false
      }
    },
    showAlert(message, type) {
      this.alertMessage = message
      this.alertType = type
    },
    forgotPassword() {
      alert('Forgot password functionality would be implemented here!')
    },
    async handleGoogleLogin() {
      // Clear previous alerts
      this.clearAlert()
      this.isLoading = true

      try {
        const result = await this.firebaseLoginWithGoogle()

        if (result.success) {
          this.showAlert('Google login successful! Redirecting...', 'success')

          // Redirect after successful login
          setTimeout(() => {
            const redirectPath = this.$route.query.redirect || '/'
            this.$router.push(redirectPath)
          }, 1500)
        } else {
          this.showAlert(this.escapeForDisplay(result.error || 'Google login failed. Please try again.'), 'error')
        }
      } catch (error) {
        console.error('Google login error:', error)
        this.showAlert('An error occurred during Google login. Please try again.', 'error')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.login-page {
  background: linear-gradient(135deg, #fef7f7 0%, #f3e5f5 100%);
  min-height: 100vh;
}

.card {
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.95);
}

.input-group-text {
  background-color: #f8f9fa;
  border-right: none;
}

.form-control {
  border-left: none;
}

.form-control:focus {
  border-left: none;
  box-shadow: none;
}

.input-group:focus-within .input-group-text {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.btn-primary {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  font-weight: 600;
}

.btn-primary:hover,
.btn-primary:focus {
  background-color: var(--primary-dark) !important;
  border-color: var(--primary-dark) !important;
}

.btn-primary:disabled {
  background-color: #ccc;
  border-color: #ccc;
}

.text-primary {
  color: var(--primary-color) !important;
}

/* Heroicons styling */
.logo-icon {
  width: 3rem;
  height: 3rem;
  color: var(--primary-color);
  stroke-width: 2;
}

.input-icon {
  width: 1rem;
  height: 1rem;
  stroke-width: 2;
}

.alert-icon {
  width: 1.25rem;
  height: 1.25rem;
  stroke-width: 2;
  color: #856404;
}

.social-icon {
  width: 1rem;
  height: 1rem;
}

@media (max-width: 576px) {
  .card-body {
    padding: 2rem !important;
  }
}
</style>
